import { useAuthenticated } from "@gettactic/hooks";
import { useMap } from "@webV2/map/MapProvider";
import { format } from "date-fns";
import { useEffect, useRef } from "react";

/**
 * Hook for syncing map state to URL in modal contexts.
 * Unlike useMapPageUrlSync, this updates the URL without using Next.js router
 * to avoid conflicting with the parent page's routing.
 */
export const useMapModalUrlSync = (
  isModal = false,
  disableTimeSync = false
) => {
  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();

  const {
    floor,
    startDate,
    endDate,
    timeMode,
    selectedResource,
    defaultTab,
    filterQuery
  } = useMap();

  const lastUrlRef = useRef<string>("");
  // Track last synced date (date-only, not time) to prevent URL updates on time-only changes
  const lastSyncedDateStr = useRef<string | null>(null);

  useEffect(() => {
    // Only run if we're in modal mode
    if (!isModal) return;

    // Build query params from current map state
    const params = new URLSearchParams();

    if (startDate && !disableTimeSync) {
      const dateStr = format(startDate, "yyyy-MM-dd");
      // Only update URL if the DATE changed (not the time)
      if (lastSyncedDateStr.current !== dateStr) {
        lastSyncedDateStr.current = dateStr;

        const date = `${dateStr}T${format(startDate, "HH:mm:ss")}`;
        params.set("date", date);
      }
    }

    if (endDate && !disableTimeSync) {
      const dateOnly = format(endDate, "yyyy-MM-dd");
      const date = `${dateOnly}T${format(endDate, "HH:mm:ss")}`;
      // Only update URL if the DATE changed (not the time)
      if (lastSyncedDateStr.current !== dateOnly) {
        lastSyncedDateStr.current = dateOnly;
        params.set("endTime", date);
      }
    }

    if (currentOffice?.id) {
      params.set("officeId", currentOffice.id);
    }

    if (floor?.id) {
      params.set("floorId", floor.id);
    }

    if (selectedResource?.id) {
      params.set("resourceId", selectedResource.id);
    }

    if (timeMode) {
      params.set("timeMode", timeMode);
    }

    if (defaultTab) {
      params.set("section", defaultTab);
    }

    if (filterQuery) {
      params.set("searchText", filterQuery);
    }

    // Set reservationModalIsOpen to indicate this is a modal view
    params.set("reservationModalIsOpen", "true");

    const queryString = params.toString();
    // Use /map as the base path to ensure reloads go to the map page
    const newUrl = `/map${queryString ? `?${queryString}` : ""}`;

    // Only update if URL actually changed
    if (newUrl !== lastUrlRef.current) {
      lastUrlRef.current = newUrl;

      // Use window.history.replaceState to update URL without triggering navigation.
      // This changes the URL to /map so reloads will go to the map page, but doesn't
      // unmount/remount the current page.
      if (typeof window !== "undefined") {
        window.history.replaceState(window.history.state, "", newUrl);
      }
    }
  }, [
    isModal,
    startDate,
    endDate,
    currentOffice?.id,
    floor?.id,
    selectedResource?.id,
    timeMode,
    disableTimeSync,
    defaultTab,
    filterQuery
  ]);
};
